import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  confirmPublicTournamentPayment,
  fetchPublicTournament,
  registerPublicTournament,
  startPublicTournamentCheckout,
} from "../../components/funtionsBD/competitionService";
import "./TournamentInscriptionPage.css";

const FORMAT = {
  groups_knockout: "Groups, then knockout",
  single_elim: "Single elimination",
  swiss: "Swiss",
};

function euros(cents) {
  return (Number(cents || 0) / 100).toFixed(2);
}

export default function TournamentInscriptionPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [entry, setEntry] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [club, setClub] = useState("");
  const [ranking, setRanking] = useState("");
  const [cats, setCats] = useState([]);
  const [company, setCompany] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchPublicTournament(token)
      .then((data) => {
        if (cancelled) return;
        setEvent(data);
        if (data?.categories?.[0]) setCats([data.categories[0].category_id]);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.message || err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    const paymentId = params.get("payment_id");
    if (params.get("paid") !== "1" || !paymentId) return undefined;
    let cancelled = false;
    confirmPublicTournamentPayment(token, paymentId)
      .then(() => fetchPublicTournament(token))
      .then((data) => {
        if (!cancelled) setEvent(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.message || err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [params, token]);

  const toggle = (id) => {
    setCats((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await registerPublicTournament(token, {
        name,
        email,
        club_name: club,
        ranking_points: ranking === "" ? null : Number(ranking),
        category_ids: cats,
        company,
      });
      setEntry(result?.registration || null);
      if (result?.registration?.registration_status === "pending_payment") {
        const pay = await startPublicTournamentCheckout(
          token,
          result.registration.tournament_player_id
        );
        if (pay?.url) {
          window.location.assign(pay.url);
          return;
        }
      }
      const fresh = await fetchPublicTournament(token);
      setEvent(fresh);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const statusLine = () => {
    const status = entry?.registration_status;
    if (status === "confirmed") return t("tourEntryConfirmed", "You are entered.");
    if (status === "pay_at_club") {
      return t("tourPayAtClub", "You are on the list. Pay the entry fee at the club.");
    }
    if (status === "waitlist") return t("tourWaitlist", "The field is full. You are on the waiting list.");
    if (status === "pending_payment") return t("tourPayCard", "Finish the card payment to confirm your entry.");
    return "";
  };

  return (
    <main className="inscribe">
      <header className="inscribe__band">
        <p>{event?.club_name || t("tourClubEvent", "Club tournament")}</p>
      </header>
      <article className="inscribe__sheet">
        <h1>
          {event?.name ||
            (error
              ? t("tourUnavailable", "This entry sheet is unavailable.")
              : t("tourLoading", "Loading the entry sheet…"))}
        </h1>
        <div className="inscribe__table" aria-hidden />
        {event ? (
          <ul className="inscribe__facts">
            <li>
              <span>{t("tourFormat", "Format")}</span>
              <strong>{t(`tourFormat_${event.format_mode}`, FORMAT[event.format_mode] || event.format_mode)}</strong>
            </li>
            <li>
              <span>{t("tourFee", "Entry fee (€)")}</span>
              <strong>{event.entry_fee_cents ? `€${euros(event.entry_fee_cents)}` : t("tourFree", "Free")}</strong>
            </li>
            <li>
              <span>{t("tourPlaces", "Places left")}</span>
              <strong>
                {event.spots_left == null
                  ? t("tourCapacityAny", "No limit")
                  : event.spots_left}
              </strong>
            </li>
            {event.event_date ? (
              <li>
                <span>{t("tourEventDate", "Date")}</span>
                <strong>
                  {event.event_date}
                  {event.start_time ? ` · ${event.start_time}` : ""}
                </strong>
              </li>
            ) : null}
          </ul>
        ) : null}

        {error ? <p className="inscribe__error">{error}</p> : null}
        {entry ? <p className="inscribe__ok">{statusLine()}</p> : null}

        {params.get("paid") === "1" && !entry ? (
          <p className="inscribe__ok">{t("tourEntryConfirmed", "You are entered.")}</p>
        ) : null}

        {event?.registration_open && !entry && params.get("paid") !== "1" ? (
          <form onSubmit={submit}>
            <label>
              {t("tourGuestName", "Name")}
              <input value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
            </label>
            <label>
              {t("email", "Email")}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </label>
            <label>
              {t("tourGuestClub", "Club")}
              <input value={club} onChange={(e) => setClub(e.target.value)} autoComplete="organization" />
            </label>
            <label>
              {t("tourRanking", "Ranking points")}
              <input inputMode="decimal" value={ranking} onChange={(e) => setRanking(e.target.value)} />
            </label>
            <fieldset>
              <legend>{t("tourCategories", "Categories")}</legend>
              <div className="inscribe__cats">
                {(event.categories || []).map((c) => (
                  <label key={c.category_id}>
                    <input
                      type="checkbox"
                      checked={cats.includes(c.category_id)}
                      onChange={() => toggle(c.category_id)}
                    />
                    {c.name}
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="inscribe__hp" aria-hidden>
              Company
              <input tabIndex={-1} autoComplete="off" value={company} onChange={(e) => setCompany(e.target.value)} />
            </label>
            <button type="submit" disabled={busy || !cats.length}>
              {event.entry_fee_cents && event.stripe_ready
                ? t("tourEnterAndPay", "Enter and pay")
                : t("tourEnter", "Enter")}
            </button>
          </form>
        ) : event && !event.registration_open ? (
          <p>{t("tourRegClosed", "Registration is closed.")}</p>
        ) : null}

        {event?.entrants?.length ? (
          <section>
            <h2>{t("tourEntered", "Entered")}</h2>
            <ul>
              {event.entrants.map((p) => (
                <li key={`${p.name}-${p.club_name || ""}`}>
                  {p.name}
                  {p.club_name ? ` · ${p.club_name}` : ""}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </article>
    </main>
  );
}
